import { useEffect, useMemo, useRef } from 'react';
import { useAtom } from 'jotai';
import { useCart } from '@/store/quick-cart/cart.context';
import {
  getInterplayersCartSignature,
  hasComboAppliedItems,
  shouldVerifyPBMCombo,
} from '@/lib/utils/pbm';
import { pbmComboVerifyAtom } from '@/store/pbm-combo-verify-atom';
import { clearPbmVerifyCache } from '@/hooks/use-pbm-cart-verify';

/**
 * Reverts stale combo prices when Interplayers PBM items leave the cart or the
 * cart composition changes (e.g. combo applied with 2 items, one removed).
 * Runs globally (no API) so totals stay correct even with the sidebar closed.
 */
export function usePbmComboRevert() {
  const { items, revertPbmComboPrices } = useCart();
  const [, setComboState] = useAtom(pbmComboVerifyAtom);
  const prevSignatureRef = useRef<string | null>(null);

  const signature = useMemo(
    () => getInterplayersCartSignature(items),
    [items],
  );

  const comboApplied = hasComboAppliedItems(items);

  useEffect(() => {
    const prevSignature = prevSignatureRef.current;
    const signatureChanged =
      prevSignature !== null && prevSignature !== signature;

    prevSignatureRef.current = signature;

    const resetComboUi = () => {
      clearPbmVerifyCache();
      setComboState({
        status: 'idle',
        savingsTotal: 0,
        discountedItemCount: 0,
      });
    };

    if (!shouldVerifyPBMCombo(items)) {
      if (comboApplied) {
        revertPbmComboPrices();
      }
      resetComboUi();
      return;
    }

    if (signatureChanged && comboApplied) {
      revertPbmComboPrices();
      resetComboUi();
    }
  }, [
    comboApplied,
    items,
    revertPbmComboPrices,
    setComboState,
    signature,
  ]);
}
